import React from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import { Link, Paragraph, Text } from '@rover/kibble/core';
import LandingHero from '@rover/react-lib/src/pages/landing/components/LandingHero';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import CitiesAndNeighborhoods from '@rover/react-lib/src/pages/shared/CitiesAndNeighborhoods/CitiesAndNeighborhoods';
import {
  SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM,
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import Guarantees from '../../components/Guarantees';
import ServiceLandingSection from '../../components/ServiceLandingSection';
import ServiceLandingServices from '../../components/ServiceLandingServices';
import ServiceTargetAudience from '../../components/ServiceTargetAudience';
import ServiceValueProposition from '../../components/ServiceValueProposition';

const OvernightDogBoarding: React.FC<Props> = ({
  locale,
  countryConfig,
  serviceLandingData,
  citiesAndNeighborhoods,
  serviceLinksWithDisplayName,
  searchResults,
  serviceTypes,
}) => {
  const { t } = useLingui();
  const serviceLandingType = ServiceLandingEnum.OVERNIGHT_DOG_BOARDING;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];
  // Tags analytics events with the real booking service type rather than the SEO-slug vocabulary
  // serviceTypeSlug above uses (e.g. 'overnight-boarding', not 'dog-boarding').
  const internalServiceTypeSlug = SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet
        title={seoTitle}
        metaDescription={seoMetaDescription}
        indexable={serviceLandingData.indexable}
      />
      <LandingHero mb="20x">
        <LandingHero.Content>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            locale={locale}
            serviceTypes={serviceTypes}
            internalServiceType={{
              slug: internalServiceTypeSlug,
              startDateSelectorDescription: t`Drop off`,
              endDateSelectorDescription: t`Pick up`,
            }}
            initialSearchFilters={initialSearchFilters}
          />
          <LandingHero.TrustPilot locale={locale} />
        </LandingHero.Content>
      </LandingHero>

      <ServiceLandingSection>
        <ServiceValueProposition>
          <ServiceValueProposition.Display>
            <Trans>More affordable than long-term kennel boarding</Trans>
          </ServiceValueProposition.Display>
          <ServiceValueProposition.List>
            <ServiceValueProposition.ListItem>
              <Text fontWeight="semibold">
                <Trans>Affordable, personalized pet care</Trans>
              </Text>
              <Paragraph mb="8x">
                <Trans>
                  Worried that dog boarding or cat boarding in a sitter’s home will be more
                  expensive than kennels? Find out{' '}
                  <Link href="/dog-sitting-rates/" target="_blank">
                    how much
                  </Link>{' '}
                  pet sitters near you typically cost.
                </Trans>
              </Paragraph>
            </ServiceValueProposition.ListItem>
            <ServiceValueProposition.ListItem>
              <Text fontWeight="semibold">
                <Trans>Pet sitters you can trust</Trans>
              </Text>
              <Paragraph mb="8x">
                <Trans>
                  Every sitter profile has been individually reviewed and approved by Rover and
                  sitters on Rover have received millions of 5-star reviews.
                </Trans>
              </Paragraph>
            </ServiceValueProposition.ListItem>
            <ServiceValueProposition.ListItem>
              <Text fontWeight="semibold">
                <Trans>Easy Meet & Greets</Trans>
              </Text>
              <Paragraph mb="8x">
                <Trans>
                  Meet your dog's perfect match before you book. Request a free in-person Meet &
                  Greet, right from your Rover inbox.
                </Trans>
              </Paragraph>
            </ServiceValueProposition.ListItem>
            <ServiceValueProposition.ListItem>
              <Text fontWeight="semibold">
                <Trans>Cute photo updates</Trans>
              </Text>
              <Paragraph mb="8x">
                <Trans>
                  Get a pic of your dog playing fetch, lounging with their sitter, or enjoying a
                  walk.
                </Trans>
              </Paragraph>
            </ServiceValueProposition.ListItem>
          </ServiceValueProposition.List>
        </ServiceValueProposition>
      </ServiceLandingSection>

      <HowBrandWorks>
        <HowBrandWorks.Display>
          <Trans>Browse to book in 3 steps flat</Trans>
        </HowBrandWorks.Display>
        <HowBrandWorks.List>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={1}>
              <Trans>Search</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>Browse sitter profiles, read reviews, and get in touch.</Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={2}>
              <Trans>Book and Pay</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>
                It’s that easy! All payments are processed through Rover's secured platform.
              </Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={3}>
              <Trans>Relax</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>You just made the best choice for your best friend.</Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
        </HowBrandWorks.List>
      </HowBrandWorks>

      <ServiceLandingSection>
        <ServiceTargetAudience>
          <ServiceTargetAudience.Display>
            <Trans>Why choose Rover instead of overnight kennels?</Trans>
          </ServiceTargetAudience.Display>
          <ServiceTargetAudience.List>
            <ServiceTargetAudience.ListItem>
              <Trans>Sitters on Rover have received millions of 5-star reviews</Trans>
            </ServiceTargetAudience.ListItem>
            <ServiceTargetAudience.ListItem>
              <Trans>
                <Text fontWeight="semibold">2+ million</Text> pet parents have booked through Rover
              </Trans>
            </ServiceTargetAudience.ListItem>
            <ServiceTargetAudience.ListItem>
              <Trans>
                <Text fontWeight="semibold">Millions</Text> of photo updates have been sent through
                Rover
              </Trans>
            </ServiceTargetAudience.ListItem>
          </ServiceTargetAudience.List>
        </ServiceTargetAudience>
      </ServiceLandingSection>

      <ServiceLandingSection>
        <Guarantees serviceTypeSlug={serviceTypeSlug} countryConfig={countryConfig} />
      </ServiceLandingSection>

      <ServiceLandingSection>
        <ServiceLandingServices {...{ serviceLandingType, serviceLinksWithDisplayName }}>
          <ServiceLandingServices.Display>
            <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
          </ServiceLandingServices.Display>
        </ServiceLandingServices>

        <CitiesAndNeighborhoods citiesOrNeighborhoods={citiesAndNeighborhoods}>
          <CitiesAndNeighborhoods.Display>
            <Trans>Thousands of pet sitters in cities and neighborhoods, including:</Trans>
          </CitiesAndNeighborhoods.Display>
        </CitiesAndNeighborhoods>
      </ServiceLandingSection>

      <ServiceLandingSection mb={['0x', '0x']} data-qa-id="service-landing-download-app-callout">
        <AppStoreCallout />
      </ServiceLandingSection>
    </>
  );
};

export default OvernightDogBoarding;
